import { CastReceiverContext } from 'chromecast-caf-receiver/cast.framework';
import { DeviceCapabilities } from 'chromecast-caf-receiver/cast.framework.system';

/**
 * Device detection and BP headers module.
 *
 * Ported from `legacy/js/utils.js` to TypeScript with strict typing.
 * Detects the Chromecast device generation (including Android TV built-in
 * devices) and exposes helper functions for correlation IDs, user-agent,
 * OS version, capability flags, and standard BP headers used by Hermes
 * and Datadog.
 */

/** Supported Cast device models. */
export enum DeviceModel {
  FirstGen = '1stGen',
  SecondGen = '2ndGen',
  ThirdGen = '3rdGen',
  FourthGen = '4thGen',
  Ultra = 'Ultra',
  NestHub = 'NestHub',
  NestHubMax = 'NestHubMax',
  /** Android TV with built-in Cast (smart TVs, not a Chromecast dongle). */
  AndroidTV = 'AndroidTV',
  NotIdentified = 'NotIdentified',
}

/** Runtime capability flags queried from the Cast platform. */
export interface DeviceCapabilityFlags {
  hdrSupported: boolean;
  dolbyVisionSupported: boolean;
  dolbyAtmosSupported: boolean;
  touchInputSupported: boolean;
  bluetoothSupported: boolean;
  displaySupported: boolean;
}

// ---------------------------------------------------------------------------
// Display-capability constants (mirrors legacy/js/utils.js)
// ---------------------------------------------------------------------------

const SUPPORTED_720P_WIDTH = 1280;
const SUPPORTED_720P_HEIGHT = 720;
const SUPPORTED_1080P_WIDTH = 1920;
const SUPPORTED_1080P_HEIGHT = 1080;
const SUPPORTED_2160P_WIDTH = 3840;
const SUPPORTED_2160P_HEIGHT = 2160;

const MP4 = 'video/mp4';
const CODEC_H264_LVL_4_1 = 'avc1.640028';
const CODEC_H264_LVL_4_2 = 'avc1.64002A';
const CODEC_H265 = 'hev1.1.6.L150.B0';
const FIRST_GEN_LAST_SUPPORTED_OS = '1.36';

// ---------------------------------------------------------------------------
// Correlation ID
// ---------------------------------------------------------------------------

const CORRELATION_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890';

/**
 * Generates a random 4-character correlation ID for request tracing.
 */
export function xCorrelationID(): string {
  const codes: number[] = [];
  for (let i = 0; i < 4; i++) {
    const index = Math.floor(Math.random() * CORRELATION_CHARS.length);
    codes.push(CORRELATION_CHARS.charCodeAt(index));
  }
  return String.fromCharCode(...codes);
}

// ---------------------------------------------------------------------------
// Navigator / OS helpers
// ---------------------------------------------------------------------------

/**
 * Returns the full `navigator.userAgent` string.
 */
export function getNavigatorUserAgent(): string {
  return navigator.userAgent;
}

/**
 * Extracts the OS version from the user-agent string.
 *
 * The Cast receiver user-agent follows the pattern:
 *   `Mozilla/5.0 ... CrKey/<os_version>`
 * For Android TV devices the last segment is `"AndroidTV"`.
 */
export function getDevicesOSVersion(): string {
  const parts = navigator.userAgent.split('/');
  return parts[parts.length - 1] ?? '';
}

// ---------------------------------------------------------------------------
// Internal OS comparison
// ---------------------------------------------------------------------------

function isOSLowerThanOrEqual(expectedOS: string): boolean {
  const osParts = getDevicesOSVersion().split('.');
  const expectedParts = expectedOS.split('.');
  const osMinor = Number(osParts[1] ?? 0);
  const expectedMinor = Number(expectedParts[1] ?? 0);
  return osMinor <= expectedMinor;
}

// ---------------------------------------------------------------------------
// Device detection helpers (require CastReceiverContext)
// ---------------------------------------------------------------------------

function getContext(): CastReceiverContext {
  return CastReceiverContext.getInstance();
}

/**
 * Returns `true` when the user-agent indicates a physical Chromecast dongle
 * (the word "Chromecast" appears in the UA model segment).
 *
 * Chromecast dongles (incl. Chromecast with Google TV):
 *   `Mozilla/5.0 (Linux; Android 12; Chromecast) ... CrKey/...`
 *
 * Android TV built-in (smart TVs):
 *   `Mozilla/5.0 (Linux; Android 12; BRAVIA XR-65A80K) ... CrKey/...`
 */
function isChromecastHardware(): boolean {
  return /\bChromecast\b/i.test(navigator.userAgent);
}

/**
 * Chromecast with Google TV (4th gen dongle): OS reports "AndroidTV" AND
 * the user-agent contains "Chromecast" in the device model segment.
 */
function isDevice4thGen(): boolean {
  return getDevicesOSVersion() === 'AndroidTV' && isChromecastHardware();
}

/**
 * Android TV built-in: OS reports "AndroidTV" but the device is NOT a
 * Chromecast dongle — it's a smart TV with Cast built-in (e.g. Sony Bravia,
 * TCL, Hisense, LG with Google TV, Philips, etc.).
 */
function isAndroidTVBuiltIn(): boolean {
  return getDevicesOSVersion() === 'AndroidTV' && !isChromecastHardware();
}

function isDeviceUltra(): boolean {
  return getContext().canDisplayType(
    MP4,
    CODEC_H265,
    SUPPORTED_2160P_WIDTH,
    SUPPORTED_2160P_HEIGHT,
    30,
  );
}

function isDevice3rdGen(): boolean {
  return getContext().canDisplayType(
    MP4,
    CODEC_H264_LVL_4_2,
    SUPPORTED_1080P_WIDTH,
    SUPPORTED_1080P_HEIGHT,
    60,
  );
}

function isDevice2ndGen(): boolean {
  return getContext().canDisplayType(
    MP4,
    CODEC_H264_LVL_4_1,
    SUPPORTED_1080P_WIDTH,
    SUPPORTED_1080P_HEIGHT,
    30,
  );
}

function isDevice1stGen(): boolean {
  return (
    getContext().canDisplayType(
      MP4,
      CODEC_H264_LVL_4_1,
      SUPPORTED_1080P_WIDTH,
      SUPPORTED_1080P_HEIGHT,
    ) && isOSLowerThanOrEqual(FIRST_GEN_LAST_SUPPORTED_OS)
  );
}

function isDeviceNestHub(): boolean {
  return getContext().canDisplayType(
    MP4,
    CODEC_H264_LVL_4_1,
    SUPPORTED_720P_WIDTH,
    SUPPORTED_720P_HEIGHT,
    60,
  );
}

function isDeviceNestHubMax(): boolean {
  return getContext().canDisplayType(
    MP4,
    CODEC_H264_LVL_4_1,
    SUPPORTED_720P_WIDTH,
    SUPPORTED_720P_HEIGHT,
    30,
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Detects and returns the current Cast device model.
 *
 * Detection order:
 *  1. Android TV platform check (both dongle and built-in) — checked first
 *     because `canDisplayType` results overlap with dongle generations.
 *  2. Codec/resolution capability checks for Chromecast dongles, from
 *     highest capability (Ultra) to lowest (Nest Hub Max).
 */
export function getDeviceModel(): DeviceModel {
  if (isDevice4thGen()) return DeviceModel.FourthGen;
  if (isAndroidTVBuiltIn()) return DeviceModel.AndroidTV;
  if (isDeviceUltra()) return DeviceModel.Ultra;
  if (isDevice3rdGen()) return DeviceModel.ThirdGen;
  if (isDevice2ndGen()) return DeviceModel.SecondGen;
  if (isDevice1stGen()) return DeviceModel.FirstGen;
  if (isDeviceNestHub()) return DeviceModel.NestHub;
  if (isDeviceNestHubMax()) return DeviceModel.NestHubMax;
  return DeviceModel.NotIdentified;
}

/**
 * Queries runtime device capabilities from the Cast platform.
 *
 * Returns `null` if the system is not ready yet. Individual flags default
 * to `false` when the platform does not expose that capability.
 */
export function getDeviceCapabilityFlags(): DeviceCapabilityFlags | null {
  const caps = getContext().getDeviceCapabilities();
  if (!caps) return null;

  return {
    hdrSupported: Boolean(caps[DeviceCapabilities.IS_HDR_SUPPORTED]),
    dolbyVisionSupported: Boolean(caps[DeviceCapabilities.IS_DV_SUPPORTED]),
    dolbyAtmosSupported: Boolean(caps[DeviceCapabilities.IS_DOLBY_ATMOS_SUPPORTED]),
    touchInputSupported: Boolean(caps[DeviceCapabilities.TOUCH_INPUT_SUPPORTED]),
    bluetoothSupported: Boolean(caps[DeviceCapabilities.BLUETOOTH_SUPPORTED]),
    displaySupported: Boolean(caps[DeviceCapabilities.DISPLAY_SUPPORTED]),
  };
}

/**
 * Returns `true` if the current device supports HDR playback.
 * Shorthand used by the player to select appropriate stream quality.
 */
export function isHdrSupported(): boolean {
  const caps = getContext().getDeviceCapabilities();
  return Boolean(caps?.[DeviceCapabilities.IS_HDR_SUPPORTED]);
}

/**
 * Returns the standard BP headers used across Hermes requests and Datadog context.
 *
 * These headers are expected by the BP backend for device identification,
 * request tracing, and analytics attribution.
 */
export function getDeviceHeaders(): Record<string, string> {
  return {
    'x-correlation-id': xCorrelationID(),
    'x-device-model': getDeviceModel(),
    'x-device-os': getDevicesOSVersion(),
    'x-user-agent': getNavigatorUserAgent(),
  };
}
